import { useEffect, useState } from "react";
import { Activity, Target } from "lucide-react";
import { api } from "@/lib/api";
import { fmtOdds, SPORT_META } from "@/lib/odds";
import { MoveChip } from "@/components/MoveChip";
import { PlayerLink } from "@/components/PlayerLink";
import { Link } from "react-router-dom";

export function SteamBoard() {
  const [d, setD] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => {
    const load = () => api.get("/odds/movement").then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, []);
  if (!d) return <div className="glass-card h-40 animate-pulse" data-testid="steam-board-loading" />;
  const rows = all ? d.movers : d.movers.slice(0, 6);
  const openLabel = new Date(d.open_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <section className="glass-card overflow-hidden" data-testid="steam-board">
      <div className="flex flex-wrap items-center gap-3 border-b border-[#26334D] px-5 py-3">
        <h2 className="font-display flex items-center gap-2 text-xl font-bold uppercase tracking-tight text-white"><Activity size={18} className="text-orange-400" /> Steam Board</h2>
        <p className="text-xs text-slate-400">{d.real ? <>Real market moves since lines opened ({openLabel}){d.last_refresh && <>, refreshed {new Date(d.last_refresh).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</>}</> : <>Line movement since the {openLabel} slate open</>} · <span className="font-mono-num text-white" data-testid="steam-lines-moved">{d.lines_moved}</span> of {d.lines_tracked} lines moved · <span className="font-mono-num text-orange-300" data-testid="steam-count">{d.steam_lines}</span> showing sharp steam</p>
        <div className="ml-auto flex items-center gap-3 text-[10px] uppercase tracking-wider text-slate-500">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-red-400" /> Price shortened</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-400" /> Price drifted</span>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-500" data-testid="steam-empty">{d.real ? "Every line is still at its opening number — moves show up here as the hourly refresh catches books adjusting." : "Lines are still at the open — steam usually shows up mid-morning once sharp money hits."}</p>
      ) : (
        <ul className="divide-y divide-[#26334D]/60" data-testid="steam-rows">
          {rows.map((r, i) => (
            <li key={r.key} data-testid="steam-row" className="fade-up flex flex-wrap items-center gap-3 px-5 py-2.5 transition-colors hover:bg-[#182030]" style={{ animationDelay: `${i * 40}ms` }}>
              <span className="font-mono-num w-5 text-xs text-slate-600">{i + 1}</span>
              <span className="label-caps w-14 shrink-0" style={{ color: SPORT_META[r.sport]?.color }}>{SPORT_META[r.sport]?.short}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white" data-testid="steam-label">{r.player ? <PlayerLink player={r.player} label={r.label} /> : r.label}</p>
                <p className="truncate text-xs text-slate-500"><Link to={`/games/${r.event_id}`} data-testid="steam-game-link" className="hover:text-emerald-300">{r.matchup}</Link> · {r.market} · {new Date(r.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</p>
              </div>
              {r.model_pick && (
                <span data-testid="steam-model-on" className="flex items-center gap-1 rounded-full border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">
                  <Target size={10} /> Model on this side{r.direction === "in" ? " · beat the number" : ""}
                </span>
              )}
              <MoveChip movement={r} />
              <span className={`font-mono-num w-14 text-right text-sm font-bold ${r.direction === "in" ? "text-red-300" : "text-emerald-300"}`}>{fmtOdds(r.current)}</span>
            </li>
          ))}
        </ul>
      )}
      {d.movers.length > 6 && (
        <button data-testid="steam-toggle-all" onClick={() => setAll((a) => !a)} className="w-full border-t border-[#26334D] py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 transition-colors hover:text-emerald-400">
          {all ? "Show top 6" : `Show all ${d.movers.length} movers`}
        </button>
      )}
    </section>
  );
}
